import React from 'react';
import { useStore } from '@/store/useStore';
import { Link } from 'react-router-dom';
import { 
  TrendingUp, 
  TrendingDown, 
  Droplets, 
  Flame, 
  Building2, 
  Activity,
  ArrowRight,
  BarChart3
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
} from 'recharts';
import { cn } from '@/lib/utils';

const formatNumber = (num) => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num?.toFixed(0) || '0';
};

const StatCard = ({ title, value, subtitle, icon: Icon, trend, color = 'primary' }) => {
  const colorClasses = {
    primary: 'text-primary bg-primary/10',
    secondary: 'text-secondary bg-secondary/10',
    success: 'text-success bg-success/10',
    danger: 'text-danger bg-danger/10',
  };

  return (
    <Card className="bg-surface border-white/10 card-hover">
      <CardContent className="p-2.5 sm:p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider mb-0.5">
              {title}
            </p>
            <p className="text-base sm:text-xl lg:text-2xl font-heading font-bold text-foreground truncate">
              {value}
            </p>
            {subtitle && (
              <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5 truncate">{subtitle}</p>
            )}
            {trend !== undefined && (
              <div className={cn(
                "flex items-center gap-1 mt-1 text-[10px] sm:text-xs font-mono",
                trend >= 0 ? "text-success" : "text-danger"
              )}>
                {trend >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                <span>{trend >= 0 ? '+' : ''}{trend.toFixed(1)}%</span>
              </div>
            )}
          </div>
          <div className={cn("p-1.5 sm:p-2 rounded flex-shrink-0", colorClasses[color])}>
            <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const Dashboard = () => {
  const { basinData, operators, counties, isLoading, priceDeck } = useStore();

  if (isLoading && !basinData) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="bg-surface border-white/10">
              <CardContent className="p-4">
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-8 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const productionTrend = basinData?.production_trend?.map(item => ({
    month: item._id,
    oil: item.total_oil,
    gas: item.total_gas / 6, // Scale for visualization
  })) || [];

  const topOperators = basinData?.top_operators || [];

  const countyData = counties?.slice(0, 9).map(c => ({
    name: c.name,
    production: c.total_oil,
    growth: c.growth_intensity,
  })) || [];

  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Page Header - Mobile optimized */}
      <div className="flex items-center justify-between mb-1">
        <div>
          <h1 className="text-lg sm:text-xl lg:text-2xl font-heading font-bold tracking-wide uppercase">
            Basin Overview
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Texas Permian Basin
          </p>
        </div>
        <Badge 
          variant="outline" 
          className="border-primary/50 text-primary font-mono text-[10px] sm:text-xs"
        >
          ${priceDeck}/bbl
        </Badge>
      </div>

      {/* KPI Cards - Compact on mobile */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
        <StatCard
          title="Total Oil Production"
          value={`${formatNumber(basinData?.total_oil_production || 0)} BBL`}
          subtitle="Last 24 months"
          icon={Droplets}
          color="primary"
        />
        <StatCard
          title="Total Gas Production"
          value={`${formatNumber(basinData?.total_gas_production || 0)} MCF`}
          subtitle="Last 24 months"
          icon={Flame}
          color="secondary"
        />
        <StatCard
          title="Active Wells"
          value={formatNumber(basinData?.active_wells || 0)}
          subtitle={`of ${formatNumber(basinData?.total_wells || 0)} total`}
          icon={Activity}
          trend={2.3}
          color="success"
        />
        <StatCard
          title="Active Operators"
          value={basinData?.total_operators || 0}
          subtitle="In basin"
          icon={Building2}
          color="primary"
        />
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 sm:gap-4">
        {/* Production Trend Chart */}
        <Card className="lg:col-span-2 bg-surface border-white/10">
          <CardHeader className="border-b border-white/5 p-2.5 sm:p-4 bg-white/[0.02]">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xs sm:text-sm font-heading uppercase tracking-widest">
                Production Trend
              </CardTitle>
              <Badge variant="outline" className="border-white/20 text-[10px] sm:text-xs font-mono">
                12 MO
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="p-2 sm:p-4">
            <div className="h-[200px] sm:h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={productionTrend}>
                  <defs>
                    <linearGradient id="oilGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#F59E0B" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="gasGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                  <XAxis 
                    dataKey="month" 
                    stroke="#52525B" 
                    tick={{ fill: '#A1A1AA', fontSize: 10, fontFamily: 'IBM Plex Mono' }}
                  />
                  <YAxis 
                    stroke="#52525B" 
                    tick={{ fill: '#A1A1AA', fontSize: 10, fontFamily: 'IBM Plex Mono' }}
                    tickFormatter={formatNumber}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#0F0F10', 
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '4px',
                      fontFamily: 'IBM Plex Mono',
                      fontSize: '12px'
                    }}
                    formatter={(value, name) => [formatNumber(value), name === 'oil' ? 'Oil (BBL)' : 'Gas (MCF)']}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="oil" 
                    stroke="#F59E0B" 
                    fill="url(#oilGradient)" 
                    strokeWidth={2}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="gas" 
                    stroke="#3B82F6" 
                    fill="url(#gasGradient)" 
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center justify-center gap-6 mt-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-primary" />
                <span className="text-xs text-muted-foreground">Oil Production</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-secondary" />
                <span className="text-xs text-muted-foreground">Gas Production</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Top Operators */}
        <Card className="bg-surface border-white/10">
          <CardHeader className="border-b border-white/5 p-2.5 sm:p-4 bg-white/[0.02]">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xs sm:text-sm font-heading uppercase tracking-widest">
                Top Operators
              </CardTitle>
              <Link to="/operators">
                <Button variant="ghost" size="sm" className="text-[10px] sm:text-xs text-muted-foreground hover:text-foreground h-6 px-2">
                  All <ArrowRight className="w-3 h-3 ml-0.5" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-white/5">
              {topOperators.map((op, idx) => (
                <Link 
                  key={op.id} 
                  to={`/operators/${op.id}`}
                  data-testid={`operator-${idx}`}
                  className="flex items-center justify-between p-2.5 sm:p-4 hover:bg-white/[0.02] transition-colors"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[10px] sm:text-xs font-mono text-muted-foreground w-3">
                      {idx + 1}
                    </span>
                    <div className="min-w-0">
                      <p className="text-xs sm:text-sm font-medium text-foreground truncate">
                        {op.name}
                      </p>
                      <p className="text-[10px] sm:text-xs text-muted-foreground font-mono">
                        {op.active_wells} wells
                      </p>
                    </div>
                  </div>
                  <Badge 
                    variant="outline" 
                    className={cn(
                      "font-mono text-[10px] flex-shrink-0 ml-2",
                      op.momentum_score >= 70 
                        ? "border-success/50 text-success" 
                        : op.momentum_score >= 40 
                          ? "border-primary/50 text-primary"
                          : "border-danger/50 text-danger"
                    )}
                  >
                    {op.momentum_score?.toFixed(0)}
                  </Badge>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-4">
        {/* County Heatmap */}
        <Card className="bg-surface border-white/10">
          <CardHeader className="border-b border-white/5 p-2.5 sm:p-4 bg-white/[0.02]">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xs sm:text-sm font-heading uppercase tracking-widest">
                County Production
              </CardTitle>
              <Badge variant="outline" className="border-white/20 text-[10px] sm:text-xs font-mono">
                TOP 9
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="p-2 sm:p-4">
            <div className="h-[200px] sm:h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={countyData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" horizontal={false} />
                  <XAxis 
                    type="number"
                    stroke="#52525B" 
                    tick={{ fill: '#A1A1AA', fontSize: 10, fontFamily: 'IBM Plex Mono' }}
                    tickFormatter={formatNumber}
                  />
                  <YAxis 
                    type="category"
                    dataKey="name"
                    stroke="#52525B" 
                    tick={{ fill: '#A1A1AA', fontSize: 10, fontFamily: 'IBM Plex Mono' }}
                    width={80}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#0F0F10', 
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '4px',
                      fontFamily: 'IBM Plex Mono',
                      fontSize: '12px'
                    }}
                    formatter={(value) => [formatNumber(value), 'Oil (BBL)']}
                  />
                  <Bar dataKey="production" radius={[0, 4, 4, 0]}>
                    {countyData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={entry.growth > 0 ? '#10B981' : '#EF4444'}
                        fillOpacity={0.7 + (index * 0.03)}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Momentum Rankings */}
        <Card className="bg-surface border-white/10">
          <CardHeader className="border-b border-white/5 p-2.5 sm:p-4 bg-white/[0.02]">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xs sm:text-sm font-heading uppercase tracking-widest">
                Momentum
              </CardTitle>
              <Link to="/forecasting">
                <Button variant="ghost" size="sm" className="text-[10px] sm:text-xs text-muted-foreground hover:text-foreground h-6 px-2">
                  Forecast <ArrowRight className="w-3 h-3 ml-0.5" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent className="p-2.5 sm:p-4">
            <div className="space-y-2 sm:space-y-3">
              {operators?.slice(0, 6).map((op, idx) => (
                <div key={op.id} className="space-y-0.5 sm:space-y-1">
                  <div className="flex items-center justify-between text-xs sm:text-sm">
                    <span className="text-foreground truncate max-w-[120px] sm:max-w-[180px]">
                      {op.name}
                    </span>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <span className={cn(
                        "text-[10px] sm:text-xs font-mono",
                        op.growth_rate >= 0 ? "text-success" : "text-danger"
                      )}>
                        {op.growth_rate >= 0 ? '+' : ''}{op.growth_rate?.toFixed(1)}%
                      </span>
                      <span className="text-[10px] sm:text-xs font-mono text-muted-foreground">
                        {op.momentum_score?.toFixed(0)}
                      </span>
                    </div>
                  </div>
                  <Progress 
                    value={op.momentum_score} 
                    className="h-1 sm:h-1.5 bg-white/5"
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
